import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { AsyncButton, CheckBoxFormField, SaveStates } from '@infl-ts/ui-advocate-components';
import { DE$ } from '@infl-ts/util-datum';
import { E$, S$ } from '@infl-ts/util-fp-ts';
import { logout } from '@infl-ts/util-hub-axios-instance';
import { logError } from '@infl-ts/util-query-errors';
import { useMutationMapper } from '@infl-ts/util-use-mutation-mapper';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormGroup, Typography } from '@mui/material';
import * as DE from '@nll/datum/lib/DatumEither';
import * as E from 'fp-ts/Either';
import { absurd, constNull, constVoid, identity, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as R from 'fp-ts/Record';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ConsentFlowVariant } from '../setup/site-config-types';
import { AcceptConsentResult } from './codecs';
import { useAcceptConsent, useConsentStatusQuery } from './queries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var AsyncButtonStatuses = [SaveStates.Saving, SaveStates.Success, SaveStates.Error, SaveStates.Default];

var ErrorMessage = function ErrorMessage(_ref) {
  var children = _ref.children;
  return /*#__PURE__*/_jsx(Typography, {
    variant: "caption",
    sx: {
      mb: 2,
      color: function color(t) {
        return t.palette.error.main;
      }
    },
    "data-element": "Typography",
    "data-component": "ErrorMessage",
    "data-source-file": "consent-modal.tsx",
    children: children
  });
};

export var ConsentModal = function ConsentModal(_ref2) {
  var hubName = _ref2.hubName,
      emailOptIn = _ref2.emailOptIn,
      options = _ref2.options,
      flowVariant = _ref2.flowVariant;

  var _useForm = useForm({
    defaultValues: {
      fields: options.map(function (o) {
        return _objectSpread(_objectSpread({}, o), {}, {
          accepted: false
        });
      }),
      emailOptIn: false
    }
  }),
      control = _useForm.control,
      handleSubmit = _useForm.handleSubmit,
      formState = _useForm.formState;

  var _useFieldArray = useFieldArray({
    control: control,
    name: 'fields'
  }),
      fields = _useFieldArray.fields;

  var _useAcceptConsent = useAcceptConsent({
    update: function update(cache, data) {
      return pipe(data, E.map(S$.prop('acceptConsent')), E.chainW(E.fromNullable('no-response-from-server')), E$.filterOrNarrowElseW(function (a) {
        return a.__typename === AcceptConsentResult.props.__typename.value;
      }, identity), E.map(S$.prop('consentStatus')), E.match(constVoid, function (consentStatus) {
        cache.writeQuery({
          query: useConsentStatusQuery.document,
          data: {
            consentStatus: consentStatus
          }
        });
      }));
    }
  }),
      _useAcceptConsent2 = _slicedToArray(_useAcceptConsent, 2),
      callAcceptConsent = _useAcceptConsent2[0],
      acceptConsentStatus = _useAcceptConsent2[1];

  var acceptConsentAsyncButtonStatus = useMutationMapper(acceptConsentStatus, function (_ref3) {
    var acceptConsent = _ref3.acceptConsent;
    return (acceptConsent === null || acceptConsent === void 0 ? void 0 : acceptConsent.__typename) === AcceptConsentResult.props.__typename.value && acceptConsent.consentStatus ? E.right(acceptConsent) : E.left(acceptConsent);
  }, AsyncButtonStatuses);

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  return /*#__PURE__*/_jsxs(Dialog, {
    open: true,
    scroll: "body",
    sx: {
      backgroundColor: function backgroundColor(t) {
        return t.palette.other.backdrop;
      }
    },
    PaperProps: {
      sx: {
        maxWidth: 'unset',
        width: {
          sm: '90%',
          md: '50%'
        },
        padding: function padding(t) {
          return t.spacing(2);
        }
      }
    },
    "data-element": "Dialog",
    "data-component": "ConsentModal",
    "data-source-file": "consent-modal.tsx",
    children: [/*#__PURE__*/_jsx(DialogTitle, {
      variant: "h3",
      sx: {
        p: 1,
        pb: 3
      },
      "data-element": "DialogTitle",
      "data-component": "ConsentModal",
      "data-source-file": "consent-modal.tsx",
      children: t('Consent to {{hubName}} Policies', {
        hubName: hubName
      })
    }), /*#__PURE__*/_jsxs(DialogContent, {
      sx: {
        pb: 2,
        px: 1,
        display: 'flex',
        flexDirection: 'column'
      },
      "data-element": "DialogContent",
      "data-source-file": "consent-modal.tsx",
      children: [pipe(acceptConsentStatus, DE$.successToOption, O.chain(O.fromPredicate(function (_ref4) {
        var acceptConsent = _ref4.acceptConsent;
        return (acceptConsent === null || acceptConsent === void 0 ? void 0 : acceptConsent.__typename) !== AcceptConsentResult.props.__typename.value || !acceptConsent.consentStatus;
      })), O.match(constNull, function () {
        logError('Consent result was unexpectedly false after user checked all required inputs');
        return /*#__PURE__*/_jsx(ErrorMessage, {
          children: t('An error occurred. Try again later.')
        });
      })), R.size(formState.errors) > 0 || DE.isFailure(acceptConsentStatus) ? /*#__PURE__*/_jsx(ErrorMessage, {
        children: t('You must consent to the following before continuing')
      }) : null, /*#__PURE__*/_jsxs(FormGroup, {
        sx: {
          ml: 1,
          mb: 1
        },
        "data-element": "FormGroup",
        "data-source-file": "consent-modal.tsx",
        children: [fields.map(function (item, index) {
          return flowVariant === ConsentFlowVariant.map.AFTER_SIGN_UP_CHECKBOXES || flowVariant === ConsentFlowVariant.map.BEFORE_SIGN_UP_CHECKBOX ? /*#__PURE__*/_jsx(CheckBoxFormField, {
            control: control,
            name: "fields.".concat(index, ".accepted"),
            label: item.label,
            required: true,
            errorMode: "highlightCheckbox",
            dangerouslySetLabelInnerHtml: true,
            sx: {
              mb: 2
            },
            checkboxSize: "small"
          }, item.id) : flowVariant === ConsentFlowVariant.map.AFTER_SIGN_UP_AGREEMENT || flowVariant === ConsentFlowVariant.map.BEFORE_SIGN_UP_AGREEMENT ? /*#__PURE__*/_jsx(Typography, {
            dangerouslySetInnerHTML: {
              __html: item.label
            }
          }, item.id) : absurd(flowVariant);
        }), emailOptIn && flowVariant !== ConsentFlowVariant.map.BEFORE_SIGN_UP_AGREEMENT ? /*#__PURE__*/_jsx(CheckBoxFormField, {
          control: control,
          name: "emailOptIn",
          label: emailOptIn,
          errorMode: "highlightCheckbox",
          dangerouslySetLabelInnerHtml: true,
          checkboxSize: "small"
        }) : null]
      })]
    }), /*#__PURE__*/_jsxs(DialogActions, {
      disableSpacing: true,
      sx: {
        flexDirection: 'column'
      },
      "data-element": "DialogActions",
      "data-source-file": "consent-modal.tsx",
      children: [/*#__PURE__*/_jsx(AsyncButton, {
        fullWidth: true,
        sx: {
          mb: 1
        },
        status: acceptConsentAsyncButtonStatus,
        text: ['Loading', 'Agreed', 'I agree', 'I agree'],
        onClick: handleSubmit(function (_ref5) {
          var fields = _ref5.fields,
              emailOptInStatus = _ref5.emailOptIn;
          return callAcceptConsent({
            variables: {
              input: {
                fields: fields.map(function (_ref6) {
                  var id = _ref6.id,
                      accepted = _ref6.accepted;
                  return {
                    id: id,
                    accepted: accepted
                  };
                }),
                receiveEmailNotification: emailOptIn ? emailOptInStatus : null
              }
            }
          });
        }),
        "data-element": "AsyncButton",
        "data-source-file": "consent-modal.tsx"
      }), /*#__PURE__*/_jsx(Button, {
        fullWidth: true,
        variant: "text",
        size: "large",
        sx: {
          color: function color(t) {
            return t.palette.text.disabled;
          }
        },
        onClick: function onClick() {
          return logout();
        },
        "data-element": "Button",
        "data-source-file": "consent-modal.tsx",
        children: t('Sign me out')
      })]
    })]
  });
};